import { useId } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import { useReviewQueue } from "../data/useClassroomWorkspace";
import ui from "./ui.module.css";

const TABS = [
  { to: "/", label: "Today" },
  { to: "/week", label: "Week" },
  { to: "/learners", label: "Learners" },
  { to: "/work", label: "Work" },
];

const tabFor = (pathname) => {
  if (pathname.startsWith("/learners")) return "/learners";
  if (pathname.startsWith("/work")) return "/work";
  if (pathname.startsWith("/week") || pathname.startsWith("/subject")) return "/week";
  if (pathname.startsWith("/tablets")) return null;
  return "/";
};

// A learner or a piece of work belongs to one class; another class starts
// from that tab's overview.
const DETAIL = ["/learners/:id", "/work/:id"];

// The class being taught stays above every tab, so Today, Week, Learners and
// Work always answer for the same class.
const WorkspaceNav = () => {
  const classes = useClassrooms();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const selectId = useId();
  const { active, classrooms } = classes;
  const queue = useReviewQueue(active.id, 1);
  const toReview = queue.data?.total || 0;
  const current = tabFor(pathname);

  const switchClass = (event) => {
    classes.choose(event.target.value);
    if (DETAIL.some((pattern) => matchPath(pattern, pathname))) navigate(current);
  };

  return (
    <nav className={ui.workspaceNav} aria-label="Classroom">
      <div className={`container ${ui.workspaceNavInner}`}>
        {classrooms.length > 1 ? (
          <span className={ui.classPicker}>
            <label htmlFor={selectId} className={ui.visuallyHidden}>
              Class
            </label>
            <select id={selectId} value={active.id} onChange={switchClass}>
              {classrooms.map((classroom) => (
                <option key={classroom.id} value={classroom.id}>
                  {classroom.name}
                </option>
              ))}
            </select>
          </span>
        ) : (
          <span className={ui.className}>{active.name}</span>
        )}
        <ul className={ui.tabs}>
          {TABS.map((tab) => (
            <li key={tab.to}>
              <Link
                to={tab.to}
                className={ui.tab}
                aria-current={current === tab.to ? "page" : undefined}
              >
                {tab.label}
                {tab.to === "/work" && toReview > 0 && (
                  <span className={ui.tabBadge}>
                    {toReview}
                    <span className={ui.visuallyHidden}> to review</span>
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default WorkspaceNav;
